import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { PackagingItemChangeHistoryEntry } from '@/shared/packagingItemChangeHistory'
import {
  formatChangeHistoryScalar,
  formatManualOverrideChangeHistorySummary,
  packagingItemChangeHistoryActionLabelKey,
  packagingItemChangeHistoryActorLabelKey,
  packagingItemChangeHistoryActorUserDisplayName,
  packagingItemChangeHistoryEntryKey,
  packagingItemChangeHistoryFieldLabelKey,
  resolveManualOverrideChangeHistorySummary,
} from '@/shared/packagingItemChangeHistory'
import { PackagingAssessmentRunDetailSheet } from './PackagingAssessmentRunDetailSheet'
import {
  formatPackagingItemAuditRunTimestamp,
  formatPackagingItemChangeHistoryTimestamp,
} from './formatPackagingItemAuditRunTimestamp'

type PackagingItemChangeHistoryTableProps = {
  history: PackagingItemChangeHistoryEntry[]
  locale: string
}

function ChangeHistoryActorCell({
  entry,
}: {
  entry: PackagingItemChangeHistoryEntry
}) {
  const { t } = useTranslation('packagingSuppliers')
  const actorLabel = t(packagingItemChangeHistoryActorLabelKey(entry.actor))
  const actorUserDisplayName =
    packagingItemChangeHistoryActorUserDisplayName(entry)

  return (
    <Badge
      variant='secondary'
      title={actorUserDisplayName ?? undefined}
    >
      {actorLabel}
    </Badge>
  )
}

function ChangeHistorySummary({
  entry,
}: {
  entry: PackagingItemChangeHistoryEntry
}) {
  const { t, i18n } = useTranslation('packagingSuppliers')
  const useChineseReason = i18n.language.startsWith('zh')

  const manualOverrideSummary = resolveManualOverrideChangeHistorySummary(entry)
  if (manualOverrideSummary != null) {
    return (
      <span className='text-sm text-muted-foreground'>
        {formatManualOverrideChangeHistorySummary({
          ...manualOverrideSummary,
          translate: (key: string, options?: Record<string, string>) =>
            t(key, options) as string,
        })}
      </span>
    )
  }

  if (useChineseReason && entry.reasonZh != null && entry.reasonZh.trim() !== '') {
    return (
      <span className='text-sm text-muted-foreground'>{entry.reasonZh}</span>
    )
  }
  if (entry.reason != null && entry.reason.trim() !== '') {
    return <span className='text-sm text-muted-foreground'>{entry.reason}</span>
  }

  return (
    <span className='text-sm text-muted-foreground'>
      {t('itemDetail.changeHistory.history.fieldsChangedCount', {
        count: entry.changes.length,
      })}
    </span>
  )
}

function ChangeHistoryEntryDetails({
  entry,
}: {
  entry: PackagingItemChangeHistoryEntry
}) {
  const { t } = useTranslation('packagingSuppliers')
  const nullLabel = t('itemDetail.changeHistory.nullValue')

  return (
    <ul className='space-y-4'>
      {entry.action === 'manual_override' &&
      entry.reason != null &&
      entry.reason.trim() !== '' ? (
        <li className='space-y-1 border-b border-border pb-4'>
          <p className='text-sm font-medium text-foreground'>
            {t('itemDetail.changeHistory.manualOverride.reasonHeading')}
          </p>
          <p className='text-sm text-muted-foreground'>{entry.reason}</p>
        </li>
      ) : null}
      {entry.changes.map((change) => {
        const fieldLabelKey = packagingItemChangeHistoryFieldLabelKey(change.field)
        let fieldLabel = change.field
        if (fieldLabelKey != null) {
          fieldLabel = t(fieldLabelKey)
        }
        const rowKey = `${change.entityType}:${change.entityId}:${change.field}:${String(change.from)}:${String(change.to)}`
        return (
          <li key={rowKey} className='space-y-1 border-b border-border pb-4 last:border-0 last:pb-0'>
            <p className='text-sm font-medium text-foreground'>{fieldLabel}</p>
            <p className='text-sm text-muted-foreground'>
              {t('itemDetail.changeHistory.detail.entityType', {
                entityType: t(
                  `itemDetail.changeHistory.entityType.${change.entityType}`
                ),
              })}
            </p>
            <p className='text-sm text-foreground'>
              {formatChangeHistoryScalar(change.from, nullLabel)}
              {' → '}
              {formatChangeHistoryScalar(change.to, nullLabel)}
            </p>
          </li>
        )
      })}
      {entry.reportBasename != null && entry.reportBasename.trim() !== '' && (
        <li className='text-sm text-muted-foreground'>
          {t('itemDetail.changeHistory.detail.reportBasename', {
            name: entry.reportBasename,
          })}
        </li>
      )}
    </ul>
  )
}

export function PackagingItemChangeHistoryTable({
  history,
  locale,
}: PackagingItemChangeHistoryTableProps) {
  const { t } = useTranslation('packagingSuppliers')
  const [selectedEntryKey, setSelectedEntryKey] = useState<string | null>(null)

  const selectedEntry = history.find(
    (entry) => packagingItemChangeHistoryEntryKey(entry) === selectedEntryKey
  )
  const selectedTitle =
    selectedEntry != null
      ? formatPackagingItemAuditRunTimestamp(selectedEntry.timestamp, locale)
      : ''

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              {t('itemDetail.changeHistory.history.columnWhen')}
            </TableHead>
            <TableHead>
              {t('itemDetail.changeHistory.history.columnWho')}
            </TableHead>
            <TableHead>
              {t('itemDetail.changeHistory.history.columnAction')}
            </TableHead>
            <TableHead>
              {t('itemDetail.changeHistory.history.columnSummary')}
            </TableHead>
            <TableHead className='w-[1%] text-end'>
              <span className='sr-only'>
                {t('itemDetail.changeHistory.history.columnActions')}
              </span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {history.map((entry) => {
            const entryKey = packagingItemChangeHistoryEntryKey(entry)
            const whenLabel = formatPackagingItemChangeHistoryTimestamp(
              entry.timestamp,
              locale
            )
            const whenTitle = formatPackagingItemAuditRunTimestamp(
              entry.timestamp,
              locale
            )
            return (
              <TableRow key={entryKey}>
                <TableCell className='font-medium' title={whenTitle}>
                  {whenLabel}
                </TableCell>
                <TableCell>
                  <ChangeHistoryActorCell entry={entry} />
                </TableCell>
                <TableCell>
                  {t(packagingItemChangeHistoryActionLabelKey(entry.action))}
                </TableCell>
                <TableCell>
                  <ChangeHistorySummary entry={entry} />
                </TableCell>
                <TableCell className='text-end'>
                  <Button
                    type='button'
                    variant='link'
                    onClick={() => {
                      setSelectedEntryKey(entryKey)
                    }}
                  >
                    {t('itemDetail.changeHistory.history.viewDetails')}
                  </Button>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      <PackagingAssessmentRunDetailSheet
        open={selectedEntry != null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedEntryKey(null)
          }
        }}
        title={selectedTitle}
      >
        {selectedEntry != null && (
          <ChangeHistoryEntryDetails entry={selectedEntry} />
        )}
      </PackagingAssessmentRunDetailSheet>
    </>
  )
}
